<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  LayoutDashboard,
  ListTodo,
  Folder,
  Network,
  Settings,
  ShieldCheck,
  LogOut,
  Boxes,
  ScrollText,
} from 'lucide-vue-next'
import { useThemeStore } from '@/stores/theme'
import { useAuthStore } from '@/stores/auth'
import { useBackendVersion } from '@/composables/useBackendVersion'
import StatusDot from '@/components/ui/StatusDot.vue'
import { avatarUrl, logout as apiLogout } from '@/api/auth'

interface NavItem {
  name: string
  to: string
  labelKey: string
  icon: typeof LayoutDashboard
}

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const theme = useThemeStore()
const auth = useAuthStore()
// 后端真实版本 (全局缓存单例请求, 与登录页共用)
const { version } = useBackendVersion()

const items: NavItem[] = [
  { name: 'dashboard', to: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { name: 'tasks', to: '/tasks', labelKey: 'nav.tasks', icon: ListTodo },
  { name: 'files', to: '/files', labelKey: 'nav.files', icon: Folder },
  { name: 'cluster', to: '/cluster', labelKey: 'nav.cluster', icon: Network },
  { name: 'settings', to: '/settings', labelKey: 'nav.settings', icon: Settings },
]

const activeName = computed(() => {
  const seg = (route.path.split('/')[1] ?? '').toLowerCase()
  return seg || 'dashboard'
})

function navigate(to: string): void {
  if (route.path !== to) {
    router.push(to).catch(() => {
      /* ignore duplicate navigation */
    })
  }
}

function toggleTheme(): void {
  theme.cycleTheme()
}

// 登出必须先调服务端 /api/web-auth/logout 清除会话 cookie — 只清本地状态的话,
// 刷新后 restoreSession 会自动重新登录 (等于没登出)。接口失败不阻断本地登出。
async function logout(): Promise<void> {
  accountMenu.value = false
  await apiLogout().catch(() => {})
  auth.clearAuth()
  router.push({ name: 'login' }).catch(() => {
    /* ignore */
  })
}

// v3.13.92 fix: 头像按钮此前单击直接 logout() — 用户点一下头像就被踢回
// 登录页 ("头像消失")。改为弹出账户菜单: 账户与安全 / 退出登录。
const accountMenu = ref(false)
const accountArea = ref<HTMLElement | null>(null)

// v3.13.92: 昵称优先展示, 副行显示登录名; 头像用 avatarV 做 cache-bust。
const hasNickname = computed(() => !!(auth.user?.nickname && auth.user.nickname.trim()))
const displayName = computed(() => {
  const n = auth.user?.nickname?.trim()
  return n && n.length > 0 ? n : (auth.user?.username ?? '?')
})
const initials = computed(() => displayName.value.slice(0, 2).toUpperCase())
const avatarSrc = computed(() => avatarUrl(auth.user?.avatarV))

function toggleAccountMenu(): void {
  accountMenu.value = !accountMenu.value
}

function goAccountSecurity(): void {
  accountMenu.value = false
  router.push('/settings').catch(() => {
    /* ignore */
  })
}

function onDocClick(e: MouseEvent): void {
  if (!accountMenu.value) return
  const area = accountArea.value
  if (area && !area.contains(e.target as Node)) accountMenu.value = false
}
function onEsc(e: KeyboardEvent): void {
  if (e.key === 'Escape') accountMenu.value = false
}
// 路由变化时收起 (push 到其它页面)
watch(() => route.fullPath, () => { accountMenu.value = false })

onMounted(() => {
  document.addEventListener('click', onDocClick, true)
  document.addEventListener('keydown', onEsc)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick, true)
  document.removeEventListener('keydown', onEsc)
})

const themeLabel = computed(() => {
  switch (theme.mode) {
    case 'light': return t('theme.light')
    case 'dark': return t('theme.dark')
    case 'auto': return t('theme.auto')
  }
})
</script>

<template>
  <aside
    class="glass-panel m-4 mr-0 flex w-60 shrink-0 flex-col rounded-2xl"
    aria-label="Sidebar"
  >
    <!-- Brand (v2 渐变 logo) -->
    <div class="flex items-center gap-2.5 px-4 pb-4 pt-5">
      <div
        class="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-[hsl(258_90%_67%)] text-sm font-extrabold text-white shadow-[0_4px_14px_rgba(77,107,254,0.4)]"
      >
        AF
      </div>
      <div class="flex flex-col leading-tight">
        <span class="text-sm font-bold tracking-wide text-fg">AFD Console</span>
        <!-- 后端真实版本 — 之前硬编码 v3.13.38, 升级后永远显示旧版本 -->
        <span v-if="version" class="text-xs text-fg-muted">{{ version }}</span>
      </div>
    </div>

    <!-- Nav -->
    <nav
      class="flex-1 overflow-y-auto px-2 py-3"
      role="navigation"
      aria-label="Primary navigation"
    >
      <ul class="space-y-1">
        <li v-for="item in items" :key="item.name">
          <button
            type="button"
            class="group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-fast"
            :class="
              activeName === item.name
                ? 'bg-gradient-to-br from-primary-500 to-[hsl(258_90%_67%)] text-white shadow-[0_4px_14px_rgba(77,107,254,0.35)]'
                : 'text-fg-muted hover:bg-bg-hover hover:text-fg'
            "
            :aria-current="activeName === item.name ? 'page' : undefined"
            @click="navigate(item.to)"
          >
            <component
              :is="item.icon"
              :size="16"
              :class="activeName === item.name ? 'text-white' : 'text-fg-subtle'"
              aria-hidden="true"
            />
            <span class="flex-1 text-left">{{ t(item.labelKey) }}</span>
          </button>
        </li>
      </ul>
    </nav>

    <!-- Footer: theme + user + WS status -->
    <div class="border-t border-border p-2 space-y-1">
      <div class="flex items-center gap-2 rounded-md px-3 py-2 text-xs text-fg-muted">
        <StatusDot status="online" />
        <span>WebSocket</span>
      </div>

      <button
        type="button"
        class="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-fg-muted hover:bg-bg-hover hover:text-fg transition-colors duration-fast"
        :title="t('theme.toggle')"
        @click="toggleTheme"
      >
        <span class="text-fg-subtle">🎨</span>
        <span class="flex-1 text-left">{{ t('common.status') }}: {{ themeLabel }}</span>
      </button>

      <!-- v3.13.92: 头像 → 账户菜单 (账户与安全 / 退出登录)。
           此前单击直接 logout(), 用户点一下头像就被登出。 -->
      <div v-if="auth.user" ref="accountArea" class="relative">
        <div
          v-if="accountMenu"
          class="absolute bottom-full left-0 right-0 mb-2 overflow-hidden rounded-md border border-border bg-bg-elevated py-1 shadow-lg"
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            class="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-fg hover:bg-bg-hover transition-colors duration-fast"
            @click.stop="goAccountSecurity"
          >
            <ShieldCheck :size="15" class="shrink-0 text-brand-500" />
            <span class="flex-1 truncate">{{ t('common.accountSecurity') }}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            class="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-fg hover:bg-bg-hover transition-colors duration-fast"
            @click.stop="logout"
          >
            <LogOut :size="15" class="shrink-0 text-fg-muted" />
            <span class="flex-1 truncate">{{ t('common.logout') }}</span>
          </button>
        </div>
        <button
          type="button"
          class="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-fg-muted hover:bg-bg-hover hover:text-fg transition-colors duration-fast"
          :aria-expanded="accountMenu"
          aria-haspopup="menu"
          @click.stop="toggleAccountMenu"
        >
          <!-- v3.13.92: 图片头像 (有 avatarV) 或首字母圆; 主行昵称, 副行登录名 -->
          <img
            v-if="avatarSrc"
            :src="avatarSrc"
            :alt="displayName"
            class="h-7 w-7 shrink-0 rounded-full object-cover"
          />
          <span
            v-else
            class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-500/15 text-xs font-semibold text-brand-600 dark:text-brand-300"
          >
            {{ initials }}
          </span>
          <span class="min-w-0 flex-1 text-left">
            <span class="block truncate text-sm text-fg">{{ displayName }}</span>
            <span v-if="hasNickname" class="block truncate text-xs text-fg-subtle">{{ auth.user.username }}</span>
          </span>
        </button>
      </div>
    </div>
  </aside>
</template>
